import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import axios from 'axios';
import './Auth.css';

console.log('[DEBUG] VITE_GOOGLE_CLIENT_ID:', import.meta.env.VITE_GOOGLE_CLIENT_ID);

const AuthForm = ({ onSuccess, onCancel, mode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const googleButtonRef = useRef(null);

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    console.log('[GoogleButton] useEffect running');
    console.log('[GoogleButton] window.google:', window.google);
    console.log('[GoogleButton] googleButtonRef.current:', googleButtonRef.current);
    console.log('[GoogleButton] client_id:', clientId);

    function renderGoogleButton() {
      if (window.google && googleButtonRef.current && clientId) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredentialResponse,
        });
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'filled_black', // changed from 'outline' to 'filled_black'
          size: 'large',
          width: 320,
          shape: 'pill',
        });
        console.log('[GoogleButton] Google button rendered');
      }
    }

    if (window.google && googleButtonRef.current && clientId) {
      renderGoogleButton();
    } else {
      const interval = setInterval(() => {
        if (window.google && googleButtonRef.current && clientId) {
          renderGoogleButton();
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, []);

  const handleGoogleCredentialResponse = async (response) => {
    try {
      setIsLoading(true);
      setError('');
      // Send credential to backend for verification/login
      const res = await axios.post(`${import.meta.env.VITE_API_URL}/api/auth/social/google`, {
        credential: response.credential
      });
      login(res.data.user, res.data.token);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Google login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const response = await axios.post(`${import.meta.env.VITE_API_URL}${endpoint}`, formData);
      login(response.data.user, response.data.token);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || `An error occurred during ${isLogin ? 'login' : 'sign up'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    // Do not reset formData here; preserve user input when switching modes
  };

  return (
    <div className="auth-container">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16 }}>
        <div ref={googleButtonRef} style={{ marginBottom: 8 }}></div>
      </div>
      <div className="auth-divider" style={{ margin: '12px 0' }}>
        <span style={{ color: "var(--color-bg-grey-dark)" }}>or</span>
      </div>
      <form onSubmit={handleSubmit}>
        {!isLogin && (
          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              placeholder="Choose a username"
              disabled={isLoading}
            />
          </div>
        )}
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder={isLogin ? "Enter your email" : "Enter your email address"}
            disabled={isLoading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            minLength="6"
            placeholder={isLogin ? "Enter your password" : "Choose a password (min. 6 characters)"}
            disabled={isLoading}
          />
        </div>
        <div style={{ height: '2em' }}></div>
        <button 
          type="submit" 
          className="submit-button"
          disabled={isLoading}
          style={{ marginTop: 8 }}
        >
          {isLoading ? 'Loading...' : (isLogin ? 'Login' : 'Sign Up')}
        </button>
      </form>
      <div className="auth-toggle" style={{ marginTop: 8 }}>
        <span 
          className="toggle-text"
          onClick={toggleMode}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              toggleMode();
            }
          }}
        >
          {isLogin ? "Don't have an account? Sign Up" : "Already have an account? Login"}
        </span>
      </div>
      {error && <div className="error-message" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
};

export default AuthForm; 